
// Librairies
import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, Signal, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';

// Services
import { InterventionService } from 'src/app/services/api/interventions/intervention.service';
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { TrackerService } from 'src/app/services/api/trackers/tracker.service';
import { AuthService } from 'src/app/services/core/auth.service';

// Interfaces
import { Intervention } from 'src/app/interfaces/api/intervention';
import { Project } from 'src/app/interfaces/api/project';
import { defaultTracker, PutTracker, Tracker } from 'src/app/interfaces/api/tracker';
import { TrackerConfig } from 'src/app/interfaces/api/trackerConfig';
import { User } from 'src/app/interfaces/api/user';

// Classes
import { Pop } from 'src/app/classes/shared/pop';
import { Tools } from 'src/app/classes/shared/tools';
import { TrackerTool } from 'src/app/classes/shared/tracker.tools';
import { TrackerConfigTool } from 'src/app/classes/shared/tracker-config.tool';
import { UiTool } from 'src/app/classes/shared/ui.tools';
import { TrackerTemplateEditor } from 'src/app/classes/shared/tracker-template-editor';

// UI
import { SearchSelectComponent } from 'projects/ui-library/src/lib/components/search-select/search-select.component';
import { ButtonComponent, SpinnerComponent } from 'ui-library';
import { TrackerConfigFormComponent } from 'src/app/components/project-settings/resources/tracker-config-form/tracker-config-form.component';
import { MatDialog } from '@angular/material/dialog';
import { TimeFormatPipe } from 'src/app/pipes/time-format.pipe';

@Component({
  selector: 'app-edit-tracker-modal',
  imports: [
    CommonModule,
    FormsModule, TranslateModule,
    MatDialogModule,
    MatInputModule,
    MatSelectModule,
    MatCardModule,
    MatIconModule,
    MatChipsModule,
    MatFormFieldModule,
    MatButtonModule,
    MatToolbarModule,
    ButtonComponent,
    SpinnerComponent,
    MatTooltipModule,
    SearchSelectComponent,
    TrackerConfigFormComponent,
    TimeFormatPipe,
  ],
  standalone: true,
  templateUrl: './edit-tracker-modal.component.html',
  styleUrls: ['./edit-tracker-modal.component.scss']
})

export class EditTrackerModal implements OnInit, OnDestroy {
  // Services
  private readonly auth: AuthService = inject(AuthService);
  private readonly trackerService: TrackerService = inject(TrackerService);
  private readonly projectService: ProjectService = inject(ProjectService);
  private readonly pop: Pop = inject(Pop);
  private readonly tools: Tools = inject(Tools);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly trackerTool: TrackerTool = inject(TrackerTool);
  private readonly trackerConfigTool: TrackerConfigTool = inject(TrackerConfigTool);
  private readonly uiTools: UiTool = inject(UiTool);
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly interventionService: InterventionService = inject(InterventionService);
  private readonly router: Router = inject(Router);
  public readonly dialogRef: MatDialogRef<EditTrackerModal> = inject(MatDialogRef<EditTrackerModal>)
  public readonly data: any = inject(MAT_DIALOG_DATA);

  // Component API
  @ViewChild('projectSelect') projectSelect!: MatSelect;

  // Variables
  tracker: any = {
    name: "",
  };
  trackerId: string = "";
  project: any;
  projects: any[] = [];
  // The device's own settings and template - its type is chosen here too (unlocked type select).
  readonly editor = new TrackerTemplateEditor();
  stats: string = "";
  editedTracker: Tracker = defaultTracker;
  title: string = 'trackers.edit_tracker'
  destroy$ = new Subject<void>();
  filteredProjects: Project[] = [];
  searchValue = '';

  // Interventions already linked to this device (Intervention.trackers contains its uuid) - read
  // only here, exactly like Ressources > Dispositif's own "Maintenance" panel (ResourcesComponent).
  // Only ever populated for an EXISTING tracker (see loadLinkedInterventions()) - a device being
  // added has no uuid yet for any Intervention to link against, so linking one is only offered
  // once it's actually been saved (see addInterventionForTracker()).
  linkedInterventions: Intervention[] = [];
  loadingLinkedInterventions: boolean = false;

  // Signals
  readonly user: Signal<User> = this.auth.currentUser;
  readonly currentProject: Signal<Project> = this.projectService.currentProjectSignal;

  constructor() {
    this.currentProject = this.data.currentProject ?? null;
  }

  async ngOnInit() {
    if (this.data.title)
      this.title = this.data.title;

    if (this.data.tracker) {
      this.tracker = this.data.tracker;
      this.tracker.project = this.data.tracker.project?.id ? { ...this.data.tracker.project } : this.data.tracker.project;
      this.editedTracker = Object.assign({}, { ...this.data.tracker });
      this.editedTracker.id = this.data.tracker._id;
      this.editedTracker.project = this.tools.toId(this.data.tracker.project);

      if (this.tracker.uuid) {
        this.getTracker(this.tracker.uuid);
        this.loadLinkedInterventions(this.tracker.uuid);
      }
    } else {
      this.editedTracker = defaultTracker;
    }
    if (this.tracker.name && this.user()?.currentProject) {
      this.getStats(this.tracker.project.id || this.tracker.project);
    }

    this.getUserProjects();
    this.initConfigDraft();

    this.trackerService.getConfigs().pipe(takeUntil(this.destroy$)).subscribe((configs: TrackerConfig[]) => {
      const firstLoad = !this.editor.configs.length;
      this.editor.configs = configs || [];
      // Only on the first load - the linked template's defaults (see initConfigDraft()) need
      // configs, but a later live emission must not discard fields the user is mid-editing.
      if (firstLoad) this.initConfigDraft();
    });
  }

  // "Interventions liées" - mirrors ResourcesComponent's own "Maintenance" panel exactly (same
  // query, same read-only display). Only called for an existing tracker (see ngOnInit()) - a
  // device being added has no uuid yet for any Intervention to reference.
  private loadLinkedInterventions(uuid: string): void {
    this.loadingLinkedInterventions = true;
    this.interventionService.searchAndCountInterventions({ trackers: uuid, sort: '-createdDate', limit: 50 })
      .pipe(takeUntil(this.destroy$)).subscribe({
        next: (res) => {
          this.linkedInterventions = res?.results ?? [];
          this.loadingLinkedInterventions = false;
        },
        error: (err: any) => {
          console.error('[EditTrackerModal] Failed to load interventions linked to tracker:', err);
          this.linkedInterventions = [];
          this.loadingLinkedInterventions = false;
        },
      });
  }

  openLinkedIntervention(interventionId: string): void {
    this.dialogRef.close();
    this.router.navigate(['/project/interventions'], { queryParams: { edit: interventionId } });
  }

  // Opens the Interventions tab with its creation drawer pre-linked to this (already saved -
  // see the *ngIf on the template) device, then redirects back here once done - exact same
  // mechanism as ResourcesComponent's own addInterventionForTracker(), just landing back on
  // /devices (see DevicesComponent's own `?tracker=` deep-link) instead of Ressources.
  addInterventionForTracker(): void {
    this.dialogRef.close();
    this.router.navigate(['/project/interventions'], { queryParams: { add: 'true', tracker: this.editedTracker.uuid, returnTo: 'devices' } });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getId(obj: any): string | undefined {
    return this.tools.toId(obj);
  }

  // lib-search-select translates whatever it's given through its own [label] input (see
  // search-select.component.html) - appending the "*" here rather than in the template keeps
  // it inside the same translated string instead of floating outside the child's own label
  // element, where it wouldn't visually read as part of the field's name.
  get projectFieldLabel(): string {
    return `${this.translate.instant('projects.list')} *`;
  }

  // Starting values: the tracker's own settings, completed with its linked template's defaults.
  private initConfigDraft() {
    const type = this.editedTracker.type ?? this.tracker.type;
    this.editor.loadTracker({ ...this.tracker, type, offlineDelay: this.editedTracker.offlineDelay ?? this.tracker.offlineDelay });
  }

  // Saving as a template alone never adds a new device - offers to add it right away.
  onSaveAsTemplate() {
    this.editor.saveAsTemplate(() => {
      if (this.editedTracker.id) return;

      this.pop.confirm(this.translate.instant('trackers.add_device_after_template_confirm'), (confirm: any) => {
        if (confirm.ok) this.saveTracker();
      });
    });
  }

  getUserProjects() {
    this.projectService.getProjectListByUser({
      archived: false,
      allProjects: ["dev", "admin"].includes(this.user().role.name),
      limit: 250
    }).pipe(takeUntil(this.destroy$)).subscribe(res => {
      this.projects = res;

      this.projects.sort((p1, p2) => {
        const name1 = p1.name.trim().toLowerCase();
        const name2 = p2.name.trim().toLowerCase();
        return name1.localeCompare(name2);
      });

      this.filteredProjects = [...this.projects];

      if (this.user() && this.user().currentProject) {
        if (!this.tracker.project) this.tracker.project = this.user().currentProject;
      }
    });
  }

  getTracker(trackerId: string) {
    this.trackerService.getMasterTrackerByUuid(trackerId).pipe(takeUntil(this.destroy$)).subscribe(res => {
      let tracker = res;

      if (!tracker) return;

      if (this.tracker.lastTrack?.id == tracker.lastTrack)
        tracker.lastTrack = this.tracker.lastTrack;

      if (this.tracker.config && !tracker.config)
        tracker.config = this.tracker.config;

      if (this.tracker.project?.id == tracker.project)
        tracker.project = this.tracker.project;

      for (let i = 0; i < tracker.projects.length; i++) {
        let pIndex = this.tracker.projects.findIndex((currProject: string) => {
          if (currProject == tracker.projects[i]) return true;

          return false;
        });

        if (pIndex >= 0)
          tracker.projects[i] = this.tracker.projects[pIndex];
      }

      this.tracker = tracker;
      this.editedTracker = Object.assign({}, { ...this.tracker });

      this.initConfigDraft();
    })
  }

  getStats(projectId = "") {
    this.stats = "";

    this.trackerTool.getStats(this.tracker, this.destroy$, projectId || this.editedTracker?.project as string).then(tracker => {
      this.tracker = tracker;
      this.stats = this.tracker.stats;
    });
  }

  onProjectChanged(projectId: string) {
    if (projectId) {
      this.editedTracker.project = projectId;
    }

    if (this.tracker.project != projectId) {
      this.getStats(projectId ?? null)
    }
  }

  // Metadata (name/project/comment/version/description/type) saves the same way as before;
  // settings are a separate step (see TrackerConfigTool.saveSettings()/
  // createTrackerWithSettings()), exactly like every other "configure a device" entry point in
  // the app.
  saveTracker() {
    if (!this.editedTracker.name) {
      this.pop.toast(this.translate.instant('trackers.no_name_error'), "danger")
      return;
    };

    // Unlike Ressources > Dispositif (already scoped to one project by the tab it's opened
    // from), this dialog manages devices across every project at once - nothing here implies
    // which one a newly added device belongs to, so leaving it unpicked would strand it outside
    // any project instead of just defaulting to "none".
    if (!this.tools.toId(this.editedTracker.project)) {
      this.pop.toast(this.translate.instant('trackers.no_project_error'), "danger")
      return;
    };

    const { type, settings, offlineDelay } = this.editor.draft;
    const appliedId = this.editor.appliedTemplate?.id;

    const tracker: PutTracker = {
      id: this.editedTracker.id,
      name: this.editedTracker.name,
      uuid: this.editedTracker.uuid,
      description: this.editedTracker.description,
      version: this.editedTracker.version,
      comment: this.editedTracker.comment,
      project: this.tools.toId(this.editedTracker.project),
      type,
      serial: type === 'KYD' ? this.editedTracker.serial : undefined,
    };
    if (appliedId) tracker.config = appliedId;

    // Projects
    const projects = [
      ...(this.editedTracker.projects ?? []).map(p => this.tools.toId(p)),
      tracker.project
    ].filter(Boolean) as string[];

    tracker.projects = [...new Set(projects)];

    const request$ = tracker.id
      ? this.trackerService.setTracker(tracker).pipe(
        switchMap(() => this.trackerConfigTool.saveSettings(
          { ...this.editedTracker, type, settings: this.tracker.settings } as Tracker,
          settings, offlineDelay, appliedId
        ))
      )
      : this.trackerConfigTool.createTrackerWithSettings(
        tracker as any, settings, offlineDelay, appliedId
      );

    request$
      .pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.tracker = this.editedTracker;
          this.pop.success(
            this.translate.instant(
              tracker.id ? 'trackers.edit_success' : 'trackers.add_success'
            )
          );
          this.dialogRef.close(this.tracker);
        },
        error: err => {
          console.error(err);
          this.pop.error(err.error?.message ?? 'Erreur inconnue');
        }
      });
  }

  isIncluded(trackerId: string) {
    let index = this.projects.findIndex(p => {
      if (p.id.toString() == trackerId.toString())
        return true;

      return false;
    })

    return index >= 0;
  }

  getProjectName(trackerId: string) {
    let index = this.projects.findIndex(p => {
      if (p.id.toString() == trackerId.toString())
        return true;

      return false;
    });

    return index >= 0 ? this.projects[index].name : "";
  }

  filterProjects(event: Event) {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();

    this.filteredProjects = this.projects.filter(project =>
      project.name.toLowerCase().includes(searchTerm)
    );
  }

  onSearchFocus(): void {
    if (this.projectSelect && !this.projectSelect.panelOpen) {
      this.projectSelect.open();
    }
  }

  onSelectOpen() {
    this.searchValue = '';
    this.filteredProjects = [...this.projects];
  }

  copy(content: string) {
    this.uiTools.copy(content);
  }
}
